import { queryClient } from '@/router'
import { useMutation, useSuspenseQuery } from '@tanstack/react-query'
import { toast } from 'sonner'

import { API_ENDPOINTS } from '@/lib/constants/api'
import { USCIS_CASE_TYPE_MAP } from '@/lib/constants/uscis/case-type'
import { useDialog } from '@/lib/hooks/use-dialog'
import { deleteVisaBulletinDataMutation } from '@/lib/queries/business/v1/case-extra/bulletin/mutations'
import { getCaseExtraInfoQuery, getCaseExtraInfoQueryKey } from '@/lib/queries/business/v3/case-extra/queries'
import { doPolling } from '@/lib/utils/function'

import { useFindTargetCase } from '../../../../hooks/use-find-target-case'

export const useVisaBulletinCard = () => {
  const { targetCase } = useFindTargetCase()
  const {
    isDialogOpen: isOpenVisaBulletinDataDeleteConfirmDialog,
    openDialog: openVisaBulletinDataDeleteConfirmDialog,
    closeDialog: closeVisaBulletinDataDeleteConfirmDialog,
  } = useDialog()

  const { data: dataCaseExtraInfo } = useSuspenseQuery(getCaseExtraInfoQuery(targetCase?.case_id))
  const { mutateAsync: deleteVisaBulletinData, isPending: isDeletingVisaBulletinDAta } =
    useMutation(deleteVisaBulletinDataMutation)

  const handleClickDeleteConfirmButton = async () => {
    const caseId = dataCaseExtraInfo?.caseId
    if (!caseId) {
      return
    }

    await deleteVisaBulletinData(
      { caseId },
      {
        onSuccess: () => {
          toast.success('Visa bulletin data has been successfully deleted!', {
            duration: 5000,
          })
          void queryClient.invalidateQueries({
            queryKey: getCaseExtraInfoQueryKey(caseId),
          })
          doPolling({
            fn: () => {
              void queryClient.invalidateQueries({
                queryKey: [API_ENDPOINTS.business.v4.case.search.index],
              })
            },
            interval: 2000,
            times: 4,
          })
        },
        onError: () => {
          toast.error('Failed to delete visa bulletin data', {
            duration: 5000,
          })
        },
        onSettled: () => {
          closeVisaBulletinDataDeleteConfirmDialog()
        },
      }
    )
  }

  // Data
  const { visaBulletin: visaBulletinData, visaBulletinSubmitted, isResidenceUS } = dataCaseExtraInfo

  const caseType = dataCaseExtraInfo.caseType
  const isI485Filed = visaBulletinSubmitted
  const isNVCAssociatedCase =
    (caseType === USCIS_CASE_TYPE_MAP['I-130'] || caseType === USCIS_CASE_TYPE_MAP['I-140']) && !isResidenceUS

  return {
    visaBulletinData,
    isI485Filed,
    isNVCAssociatedCase,
    isOpenVisaBulletinDataDeleteConfirmDialog,
    isDeletingVisaBulletinDAta,
    openVisaBulletinDataDeleteConfirmDialog,
    closeVisaBulletinDataDeleteConfirmDialog,
    handleClickDeleteConfirmButton,
  }
}
